import { useEffect } from "react";
import { X } from "@/components/icons";
import Portal from "./Portal";

/**
 * AppleModal — Modal reusable con el estilo Apple ID.
 *
 * Uso:
 *   <AppleModal
 *     open={open}
 *     onClose={onClose}
 *     icon={<User weight="regular" />}
 *     title="Nombre"
 *     testid="modal-nombre"
 *   >
 *     ...contenido...
 *   </AppleModal>
 */
export default function AppleModal({
  open,
  onClose,
  icon,
  title,
  subtitle,
  children,
  footer,
  testid = "apple-modal",
  size = "md", // sm | md | lg
  compact = false, // reduce header & padding
  step, // 1-based current step (optional)
  totalSteps, // total number of steps (optional, activates step-dots)
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const width =
    size === "sm"
      ? "max-w-[420px]"
      : size === "lg"
        ? "max-w-[640px]"
        : size === "xl"
          ? "max-w-[1080px]"
          : "max-w-[520px]";

  return (
    <Portal>
      <div
        className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/15 backdrop-blur-md"
        data-testid={`${testid}-overlay`}
        onClick={onClose}
      >
        <div
          className={`apple-modal-card w-full ${width} bg-white rounded-[24px] ${compact ? "p-5" : "p-8"} relative`}
          data-testid={testid}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close top-left */}
          <button
            type="button"
            onClick={onClose}
            data-testid={`${testid}-close`}
            className="absolute top-4 left-4 w-8 h-8 flex items-center justify-center rounded-full hover:bg-black/[0.05] transition text-black/70"
            title="Cerrar"
          >
            <X className="w-4 h-4" strokeWidth={1.75} weight="regular" />
          </button>

          {/* Icon + Title */}
          <div className={`flex flex-col items-center ${compact ? "mt-2 mb-3" : "mt-4 mb-6"}`}>
            {icon && (
              <div className={`${compact ? "w-9 h-9 mb-1.5" : "w-11 h-11 mb-3"} flex items-center justify-center text-[#0071E3]`}>
                {icon}
              </div>
            )}
            <h2
              data-testid={`${testid}-title`}
              className={`${compact ? "text-[19px]" : "text-[24px]"} font-semibold text-black tracking-tight`}
            >
              {title}
            </h2>
            {subtitle && (
              <p className={`${compact ? "text-[12px]" : "text-[13px]"} text-black/55 mt-1 text-center`}>
                {subtitle}
              </p>
            )}
            {totalSteps > 1 && (
              <div
                className="mt-4 flex items-center justify-center gap-1.5"
                data-testid={`${testid}-steps`}
              >
                {Array.from({ length: totalSteps }).map((_, i) => (
                  <div
                    key={i}
                    className={`h-1 rounded-full transition-all ${
                      i + 1 === step
                        ? "w-6 bg-[#0071E3]"
                        : i + 1 < step
                          ? "w-3 bg-[#0071E3]/40"
                          : "w-3 bg-black/10"
                    }`}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Body */}
          <div className="space-y-3">{children}</div>

          {/* Footer */}
          {footer && <div className="mt-6">{footer}</div>}
        </div>
      </div>
    </Portal>
  );
}

/**
 * AppleField — Input con label flotante (top-left) al estilo Apple ID.
 * Muestra un check verde a la derecha cuando `valid` está en true.
 *
 * Uso:
 *   <AppleField label="First name" value={v} onChange={setV} valid={ok} />
 */
export function AppleField({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
  valid = false,
  optional = false,
  autoFocus = false,
  testid,
  disabled = false,
}) {
  return (
    <label className="block">
      <div
        className={`relative rounded-xl border transition ${
          disabled
            ? "border-black/10 bg-black/[0.02]"
            : "border-black/20 focus-within:border-[#0071E3] focus-within:ring-4 focus-within:ring-[#0071E3]/10"
        }`}
      >
        <span className="absolute top-2 left-4 text-[11px] text-black/50">
          {label}
          {optional && " (opcional)"}
        </span>
        <input
          type={type}
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder={placeholder}
          autoFocus={autoFocus}
          disabled={disabled}
          data-testid={testid}
          className="w-full bg-transparent pt-6 pb-2 px-4 pr-10 text-[15px] text-black placeholder-black/25 outline-none disabled:cursor-not-allowed disabled:text-black/40"
        />
        {valid && (
          <span
            className="absolute right-4 top-1/2 -translate-y-1/2 text-emerald-600"
            data-testid={`${testid}-valid`}
          >
            <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
              <path
                fillRule="evenodd"
                d="M16.7 5.3a1 1 0 010 1.4l-8 8a1 1 0 01-1.4 0l-4-4a1 1 0 111.4-1.4L8 12.6l7.3-7.3a1 1 0 011.4 0z"
                clipRule="evenodd"
              />
            </svg>
          </span>
        )}
      </div>
    </label>
  );
}

/**
 * AppleModalActions — Contenedor estándar para los botones inferiores.
 * Botones estilo Apple pill: primario azul, secundario negro/outline.
 */
export function AppleModalActions({ children }) {
  return (
    <div className="flex flex-col-reverse sm:flex-row items-stretch gap-2 pt-2">
      {children}
    </div>
  );
}

export function AppleModalPrimaryButton({
  children,
  onClick,
  loading,
  disabled,
  testid,
  type = "button",
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      data-testid={testid}
      className="flex-1 h-11 rounded-full bg-[#0071E3] hover:bg-[#0077ED] text-white text-[14px] font-semibold transition disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
    >
      {loading ? (
        <span className="w-4 h-4 border-2 border-white/70 border-t-transparent rounded-full animate-spin" />
      ) : (
        children
      )}
    </button>
  );
}

export function AppleModalSecondaryButton({
  children,
  onClick,
  testid,
  type = "button",
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      data-testid={testid}
      className="flex-1 h-11 rounded-full bg-white border border-black/15 hover:bg-black/[0.03] text-black text-[14px] font-medium transition inline-flex items-center justify-center"
    >
      {children}
    </button>
  );
}
